import type { ConfigurationFlag } from '@cvo/domain';
import type { RuntimeFeatures } from '~/types/configurationFlags';

/** Globally fetch booking configuration flags for authenticated users */

function isFeatureEnabled(feature?: ConfigurationFlag): boolean {
  return feature?.enabled ?? false;
}

export default defineNuxtPlugin({
  name: 'configuration-features-fetch',
  // The flags belong to the selected subscription, which is only known once subscriptions are fetched.
  dependsOn: ['subscription-fetch'],
  setup: async () => {
    const api = useApi();
    const subscriptionStore = useSubscriptionStore();
    const o11y = useO11y();

    function fetchFlags<T>(promise: Promise<{ data: { features: T } }>, action: string): Promise<T> {
      return promise
        .then(res => res.data.features)
        .catch((error) => {
          console.error(`Error fetching ${action} flags`, error);
          o11y.reportError(error, { tags: { action } });
          return {} as T;
        });
    }

    async function fetchRuntimeFeatures(subscriptionId: string): Promise<RuntimeFeatures> {
      const [bookingFlags, subscriptionFlags] = await Promise.all([
        fetchFlags(api.booking.getFeatures(subscriptionId), 'fetch-booking-features'),
        fetchFlags(api.subscription.getFeatures(subscriptionId), 'fetch-subscription-features'),
      ]);

      // Typed as RuntimeFeatures so a new server-driven flag left unmapped is a compile error.
      return {
        multiBeneficiary: isFeatureEnabled(subscriptionFlags.multiBeneficiary),
        // Backend-controlled flag with no FE gating logic yet; kept mapped since the BFF sends it.
        connectingFlights: isFeatureEnabled(bookingFlags.connectingFlights),
        oneTimePurchaseAncillaries: isFeatureEnabled(bookingFlags.oneTimePurchaseAncillaries),
        quotaBreakdown: isFeatureEnabled(bookingFlags.quotaBreakdown),
      };
    }

    // Switching subscription swaps its flags; a response for a subscription left meanwhile is dropped.
    watch(() => subscriptionStore.selectedSubscriptionId, async (subscriptionId, _previousId, onCleanup) => {
      if (!subscriptionId) return;

      let isStale = false;
      onCleanup(() => {
        isStale = true;
      });

      const features = await fetchRuntimeFeatures(subscriptionId);
      if (!isStale) updateAppConfig({ features });
    });

    if (!subscriptionStore.selectedSubscriptionId) {
      console.warn('[booking-features] No subscriptionId available, skipping feature fetch');
      return;
    }

    const features = await fetchRuntimeFeatures(subscriptionStore.selectedSubscriptionId);
    updateAppConfig({ features });
  },
});
